import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  ArrowUpRight,
  Ship,
  PackageCheck,
  ClipboardCheck,
  Warehouse,
  FileCheck2,
  Truck,
  Boxes,
  RefreshCw,
  Landmark,
  SearchCheck,
  Route,
  BadgeCheck,
  Anchor,
  Container,
  MapPinned,
  BarChart3,
  Handshake,
  RadioTower,
  Building2,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const heroBg = "/assets/images/bg2.jpeg";
const sectionBg = "/assets/images/bg2.jpeg";
const portalLink = "/portal/login?service=operations";

const focusAreas = [
  {
    title: "Cargo Coordination",
    text: "Supporting approved cargo movement, vessel notices, import activities, export activities and logistics requests within Free Zones.",
    icon: Ship,
  },
  {
    title: "Inventory Support",
    text: "Providing access for stock updates, warehouse records, reconciliation, movement history and operational documentation.",
    icon: Warehouse,
  },
  {
    title: "Operational Clearance",
    text: "Helping licensed enterprises process requests connected to movement, transfer, maintenance, inspection and close-out.",
    icon: ClipboardCheck,
  },
];

const digitalServices = [
  {
    title: "Operational Approval",
    text: "For licensed enterprises requesting approval for regulated operational activities within the Free Zone.",
    icon: FileCheck2,
  },
  {
    title: "Cargo Movement",
    text: "For cargo entry, release, movement, vessel coordination, import support and export-related requests.",
    icon: Container,
  },
  {
    title: "Goods Transfer",
    text: "For transfer requests between licensees, approved facilities or Free Zone locations.",
    icon: Boxes,
  },
  {
    title: "Inventory Update",
    text: "For stock records, movement records, warehouse updates and operational reconciliation.",
    icon: BarChart3,
  },
];

const serviceCoverage = [
  "Cargo movement requests",
  "Vessel arrival coordination",
  "Import support",
  "Export support",
  "Goods transfer requests",
  "Free Zone transfer support",
  "Repairs and maintenance requests",
  "Inventory and stock updates",
];

const operationsPathway = [
  {
    title: "Select",
    text: "User chooses the required Operations service, such as cargo movement, transfer, clearance or inventory update.",
  },
  {
    title: "Submit",
    text: "The operational request is submitted with company details, cargo information and supporting documents.",
  },
  {
    title: "Review",
    text: "Operations reviews the request for completeness, licence status, activity alignment and compliance requirements.",
  },
  {
    title: "Coordinate",
    text: "Where required, the request is coordinated with relevant agencies, terminals, operators or internal service units.",
  },
  {
    title: "Close Out",
    text: "Approved requests are updated, archived and tracked for operational records and future reference.",
  },
];

const supportGroups = [
  {
    title: "Licensed Enterprises",
    text: "For companies operating within approved Oil and Gas Free Zones.",
    icon: Building2,
  },
  {
    title: "Shipping Agents",
    text: "For vessel notices, manifests, cargo documents and arrival coordination.",
    icon: Anchor,
  },
  {
    title: "Terminal Operators",
    text: "For cargo handling, warehouse release, storage, inspection and movement support.",
    icon: Truck,
  },
  {
    title: "Service Providers",
    text: "For vendors and approved operators requiring operational access or clearance.",
    icon: Handshake,
  },
];

const serviceHighlights = [
  {
    title: "Movement Control",
    text: "Supports orderly movement of goods across approved Free Zone operational channels.",
    icon: Route,
  },
  {
    title: "Document Flow",
    text: "Provides access for request submission, document upload and operational status tracking.",
    icon: FileCheck2,
  },
  {
    title: "Agency Linkage",
    text: "Supports coordination with relevant operational agencies and Free Zone stakeholders.",
    icon: Landmark,
  },
  {
    title: "Records Support",
    text: "Helps maintain request history, approvals, close-out records and inventory updates.",
    icon: RefreshCw,
  },
];

const Operations = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.2,
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        delay: 0.2,
        duration: 1,
      });

      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 55,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 86%",
          },
        });
      });

      gsap.utils.toArray(".card-animate").forEach((el, index) => {
        gsap.from(el, {
          opacity: 0,
          y: 45,
          delay: index * 0.05,
          duration: 0.75,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 90%",
          },
        });
      });

      gsap.to(".floating-orb-1", {
        y: 45,
        x: -25,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".floating-orb-2", {
        y: -45,
        x: 25,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      <section className="relative min-h-screen flex items-center overflow-hidden text-white">
        <img
          src={heroBg}
          alt="OGFZA Operations Service"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-[#041018]/85" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/95 via-[#026B07]/70 to-[#F2AF11]/30" />

        <div className="floating-orb-1 absolute top-0 left-0 w-[520px] h-[520px] rounded-full bg-[#082567]/45 blur-3xl" />
        <div className="floating-orb-2 absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#F2AF11]/20 blur-3xl" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:80px_80px]" />

        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#F2AF11]" />
            OGFZA Service Area
          </div>

          <h1
            className="hero-title text-6xl md:text-8xl font-black leading-[0.95] max-w-5xl"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Operations Service
          </h1>

          <p className="hero-text mt-8 max-w-3xl text-white/85 text-lg leading-[1.9]">
            Facilitating efficient Free Zone operations through cargo
            coordination, operational approvals, goods transfer support,
            inventory updates and regulatory service access.
          </p>

          <div className="hero-text flex flex-wrap gap-4 mt-10">
            <Link
              to={portalLink}
              className="inline-flex items-center gap-3 bg-[#F2AF11] text-[#082567] px-7 py-4 rounded-full font-black hover:bg-white transition-all duration-300"
            >
              Open Operations Portal
              <ArrowUpRight size={18} />
            </Link>

            <Link
              to="/contact-help/customer-service"
              className="inline-flex items-center gap-3 bg-white/10 border border-white/20 backdrop-blur-md text-white px-7 py-4 rounded-full font-black hover:bg-white hover:text-[#082567] transition-all duration-300"
            >
              Contact Support
              <ArrowUpRight size={18} />
            </Link>
          </div>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#F2AF11] transition-colors"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <Link
              to="/services"
              className="hover:text-[#F2AF11] transition-colors"
            >
              Services
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#F2AF11]">Operations</span>
          </div>
        </div>
      </section>

      <section className="relative -mt-24 z-20 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {focusAreas.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="card-animate group bg-white rounded-[34px] p-8 border border-slate-200 shadow-[0_24px_80px_-50px_rgba(8,37,103,0.55)] hover:-translate-y-2 hover:border-[#F2AF11]/70 transition-all duration-500"
                >
                  <div className="w-16 h-16 rounded-[24px] bg-[#082567] text-white flex items-center justify-center mb-7 group-hover:bg-[#F2AF11] group-hover:text-[#082567] transition-all duration-500">
                    <Icon size={28} />
                  </div>

                  <h3
                    className="text-2xl font-black text-[#082567] mb-4"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    {item.title}
                  </h3>

                  <p className="text-slate-600 leading-[1.8] text-[15.5px]">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-[#F2AF11]/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-[#026B07]/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-16 items-center">
          <div className="relative reveal-up">
            <div className="absolute -top-6 -left-6 w-28 h-28 bg-[#F2AF11] rounded-full" />
            <div className="absolute -bottom-6 -right-6 w-28 h-28 bg-[#026B07] rounded-[30px]" />

            <div className="relative rounded-[38px] overflow-hidden bg-white border border-slate-200 p-3 shadow-[0_30px_90px_-55px_rgba(8,37,103,0.7)]">
              <img
                src={sectionBg}
                alt="OGFZA Operations Logistics"
                className="w-full h-[520px] object-cover rounded-[30px]"
              />

              <div className="absolute inset-3 rounded-[30px] bg-gradient-to-t from-[#041018]/80 via-[#041018]/10 to-transparent" />

              <div className="absolute bottom-8 left-8 right-8">
                <div className="inline-flex items-center gap-2 bg-white text-[#082567] px-5 py-3 rounded-full font-black shadow-lg">
                  <BadgeCheck size={18} className="text-[#026B07]" />
                  Free Zone Operations Support
                </div>
              </div>
            </div>
          </div>

          <div className="reveal-up">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Service Overview
            </p>

            <h2
              className="text-4xl md:text-5xl font-black leading-tight text-[#082567] mb-6"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              A Digital Access Point for Operational Requests
            </h2>

            <div className="w-24 h-1.5 bg-[#F2AF11] rounded-full mb-7" />

            <div className="space-y-5 text-slate-700 leading-[1.9] text-[16px]">
              <p>
                The Operations Service supports licensed enterprises and
                approved stakeholders with structured access to operational
                requests within OGFZA-regulated Free Zones.
              </p>

              <p>
                This service area introduces cargo movement, goods transfer,
                inventory support, operational clearance and coordination
                services on the main website, while the portal handles formal
                submissions and tracking.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-9">
              <div className="rounded-[24px] bg-[#FAFBFC] border border-slate-200 p-6">
                <MapPinned className="text-[#026B07] mb-4" size={28} />
                <h3 className="font-black text-[#082567] text-lg">
                  Clear Guidance
                </h3>
                <p className="text-slate-600 text-sm leading-[1.7] mt-2">
                  Helps investors understand the right operational service path.
                </p>
              </div>

              <div className="rounded-[24px] bg-[#082567] p-6 text-white">
                <RadioTower className="text-[#F2AF11] mb-4" size={28} />
                <h3 className="font-black text-lg">Portal Workflow</h3>
                <p className="text-white/75 text-sm leading-[1.7] mt-2">
                  Central login with a dedicated Operations dashboard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#FAFBFC]">
        <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#082567_1px,transparent_1px),linear-gradient(to_bottom,#082567_1px,transparent_1px)] bg-[size:70px_70px]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-12 items-start">
            <div className="reveal-up lg:sticky lg:top-28">
              <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
                Digital Access
              </p>

              <h2
                className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Operations Services Available Through the Portal
              </h2>

              <p className="text-slate-600 leading-[1.9] mt-6">
                The public website explains the Operations Service area. The
                portal manages request submission, document upload, service
                routing, operational review and tracking.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {digitalServices.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className="card-animate rounded-[32px] bg-white p-8 border border-slate-200 hover:-translate-y-2 hover:border-[#F2AF11]/70 hover:shadow-[0_24px_80px_-58px_rgba(8,37,103,0.6)] transition-all duration-500"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-[#F2AF11]/15 text-[#082567] flex items-center justify-center mb-6 border border-[#F2AF11]/25">
                      <Icon size={26} />
                    </div>

                    <h3
                      className="text-2xl font-black text-[#082567] leading-tight mb-4"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {item.title}
                    </h3>

                    <p className="text-slate-600 leading-[1.8] text-[15.5px]">
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-1/2 bg-[#082567]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-0 rounded-[38px] overflow-hidden shadow-[0_30px_90px_-60px_rgba(8,37,103,0.7)]">
            <div className="bg-[#082567] text-white p-8 md:p-12">
              <p className="text-[#F2AF11] font-black tracking-[0.2em] uppercase text-xs mb-5">
                Service Coverage
              </p>

              <h2
                className="text-4xl md:text-5xl font-black leading-tight"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Built for Licensed Operational Activities
              </h2>

              <p className="text-white/75 leading-[1.9] mt-6">
                Operations support applies to approved enterprises, operators,
                agents, terminals and service providers involved in regulated
                movement, transfer, inventory and clearance activities.
              </p>

              <Link
                to={portalLink}
                className="inline-flex items-center gap-3 bg-[#F2AF11] text-[#082567] px-7 py-4 rounded-full font-black hover:bg-white transition-all duration-300 mt-8"
              >
                Continue to Portal
                <ArrowUpRight size={18} />
              </Link>
            </div>

            <div className="bg-white p-8 md:p-12 grid sm:grid-cols-2 gap-4">
              {serviceCoverage.map((item, index) => (
                <div
                  key={index}
                  className="card-animate rounded-[24px] border border-slate-200 p-5 hover:border-[#F2AF11]/70 hover:-translate-y-1 transition-all duration-500"
                >
                  <Truck className="text-[#026B07] mb-4" size={24} />
                  <p className="text-[#082567] font-black leading-tight">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#FAFBFC] overflow-hidden">
        <div className="absolute -top-24 right-0 w-[520px] h-[520px] bg-[#F2AF11]/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-4xl reveal-up mb-16">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Service Highlights
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Supporting Movement, Records and Operational Flow
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceHighlights.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="card-animate group rounded-[30px] bg-white p-7 border border-slate-200 hover:-translate-y-2 hover:border-[#F2AF11]/70 hover:shadow-[0_24px_80px_-58px_rgba(8,37,103,0.6)] transition-all duration-500"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#082567] text-white flex items-center justify-center mb-6 group-hover:bg-[#F2AF11] group-hover:text-[#082567] transition-all duration-500">
                    <Icon size={25} />
                  </div>

                  <h3
                    className="text-xl font-black text-[#082567] leading-tight mb-4"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    {item.title}
                  </h3>

                  <p className="text-slate-600 leading-[1.8] text-[15px]">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-white overflow-hidden">
        <img
          src={sectionBg}
          alt="OGFZA Operations Support"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.06]"
        />
        <div className="absolute inset-0 bg-white/94" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-4xl reveal-up mb-16">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Investor Pathway
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              From Request Selection to Operational Close-Out
            </h2>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-[42px] left-0 right-0 h-1 bg-slate-200" />

            <div className="grid md:grid-cols-5 gap-6">
              {operationsPathway.map((step, index) => (
                <div key={index} className="card-animate relative">
                  <div className="w-20 h-20 rounded-full bg-[#082567] text-white border-[6px] border-white flex items-center justify-center font-black text-xl mb-6 relative z-10">
                    {index + 1}
                  </div>

                  <div className="rounded-[28px] bg-white border border-slate-200 p-6 h-full hover:-translate-y-2 hover:border-[#F2AF11]/70 transition-all duration-500">
                    <h3
                      className="text-2xl font-black text-[#082567] mb-3"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {step.title}
                    </h3>

                    <p className="text-slate-600 leading-[1.8] text-sm">
                      {step.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#FAFBFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-10 items-center">
            <div className="reveal-up">
              <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
                Who This Supports
              </p>

              <h2
                className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                For Enterprises, Agents, Operators and Service Providers
              </h2>

              <p className="text-slate-600 leading-[1.9] mt-6">
                Operations support is relevant to licensed Free Zone
                enterprises, developers, concessionaires, shipping agents,
                terminal operators, vendors and service providers involved in
                approved operational activities.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {supportGroups.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className="card-animate rounded-[28px] bg-white border border-slate-200 p-7 hover:-translate-y-2 hover:border-[#F2AF11]/70 transition-all duration-500"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-[#F2AF11]/15 text-[#082567] flex items-center justify-center mb-5">
                      <Icon size={24} />
                    </div>

                    <h3
                      className="text-xl font-black text-[#082567] leading-tight mb-3"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {item.title}
                    </h3>

                    <p className="text-slate-600 leading-[1.7] text-sm">
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-[40px] bg-[#082567] p-8 md:p-12 shadow-[0_26px_90px_-60px_rgba(8,37,103,0.65)]">
            <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-[#F2AF11]/20 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-[380px] h-[380px] rounded-full bg-[#026B07]/30 blur-3xl" />
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:70px_70px]" />

            <div className="relative z-10 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
              <div className="reveal-up">
                <p className="text-[#F2AF11] font-black tracking-[0.2em] uppercase text-xs mb-5">
                  Central Portal Access
                </p>

                <h2
                  className="text-4xl md:text-5xl font-black text-white leading-tight"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Access the Operations Dashboard Through the OGFZA Portal
                </h2>

                <p className="text-white/75 leading-[1.9] mt-5 max-w-3xl">
                  All service areas use one central login. After authentication,
                  Operations users are directed to the Operations dashboard for
                  cargo requests, goods transfer, inventory updates, operational
                  clearances and request tracking.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row lg:flex-col gap-4">
                <Link
                  to={portalLink}
                  className="inline-flex items-center justify-center gap-3 bg-[#F2AF11] text-[#082567] px-8 py-4 rounded-full font-black hover:bg-white transition-all duration-300"
                >
                  Open Operations Portal
                  <ArrowUpRight size={18} />
                </Link>

                <Link
                  to="/services"
                  className="inline-flex items-center justify-center gap-3 bg-white/10 border border-white/20 text-white px-8 py-4 rounded-full font-black hover:bg-white hover:text-[#082567] transition-all duration-300"
                >
                  View Other Services
                  <ArrowUpRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Operations;